import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { makeStyles, IconButton } from '@material-ui/core'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import clsx from 'clsx'
import Icon from '../../../components/atoms/Icon'
import FadeIn from '../../../components/molecules/Transitions/FadeIn'
import ThreadMenu, { useThreadMenu } from './common/menu/ThreadMenu'

dayjs.extend(relativeTime)

const useStyles = makeStyles((theme) => ({
  threadItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--padding-sm) var(--padding-md)',
    marginLeft: '-4px',
    marginRight: '-4px',
    marginBottom: 0,
    fontWeight: 'lighter',
    textDecoration: 'none',
    color: 'var(--dai-color-text)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&.active': {
      backgroundColor: 'var(--dai-color-highlight)',
      color: 'var(--dai-contrast-highlight)',
      fill: 'var(--dai-contrast-highlight)',
      '& .__thread-title': {
        color: 'var(--dai-contrast-highlight)',
        fill: 'var(--dai-contrast-highlight)'
      },
      '& .__thread-meta': {
        color: 'var(--dai-contrast-highlight)',
        fill: 'var(--dai-contrast-highlight)'
      }
    },
    '& .__thread-title': {
      fontSize: 'var(--font-size-sm)',
      fontWeight: 100,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: 'var(--dai-color-text)'
    },
    '& .__thread-meta': {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-neutral-600)',
      fontWeight: 'normal', // Override default bold
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    }
  },
  threadContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  threadTitle: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'var(--dai-color-text)'
  },
  menuButton: {
    padding: '4px',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    '$threadItem:hover &': {
      opacity: 1
    },
    '$threadItem.active &': {
      opacity: 1
    }
  }
}))

function ThreadItem ({ thread, isActive, url, onRename, onDelete }) {
  const classes = useStyles()
  const threadMenu = useThreadMenu(onRename, onDelete)

  // Extract first user message as title
  const firstMessage = thread.messages?.find(m => m.actor === 'user')
  const title = thread.name || firstMessage?.message?.substring(0, 100) || 'Untitled Chat'

  return (
    <FadeIn>
      <Link
        to={`${url}/chat/${thread.threadId}`}
        className={clsx('rich', `${classes.threadItem} ${isActive ? 'active' : ''}`)}
      >
        <div className={classes.threadContent}>
          <div className='__thread-title'>
            {title}
          </div>
        </div>
        <IconButton
          className={classes.menuButton}
          size='small'
          onClick={threadMenu.handleMenuOpen}
        >
          <Icon name='vertDots' style={{ fontSize: '18px' }} />
        </IconButton>
      </Link>

      <ThreadMenu
        subject={thread}
        menuAnchor={threadMenu.menuAnchor}
        onClose={threadMenu.handleMenuClose}
        onRename={threadMenu.handleRename}
        onDelete={threadMenu.handleDelete}
      />
    </FadeIn>
  )
}

ThreadItem.propTypes = {
  thread: PropTypes.shape({
    threadId: PropTypes.string.isRequired,
    name: PropTypes.string,
    lastActivity: PropTypes.string,
    messages: PropTypes.arrayOf(PropTypes.shape({
      actor: PropTypes.string,
      message: PropTypes.string
    }))
  }).isRequired,
  isActive: PropTypes.bool,
  url: PropTypes.string.isRequired,
  onRename: PropTypes.func,
  onDelete: PropTypes.func
}

ThreadItem.defaultProps = {
  isActive: false,
  onRename: null,
  onDelete: null
}

export default ThreadItem
